/// <reference path="../../ui/types.d.ts" />
import { FC, Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { catchRejection, checkOrigin, checkResponse, reloadLocation, showToast } from '../lib/crowdin-iframe-context';
import { useFieldDependencies } from '../lib/field-dependencies';

interface ConfigField {
    key?: string;
    label?: string;
    labelHtml?: string;
    helpText?: string;
    helpTextHtml?: string;
    type?: string;
    defaultValue?: any;
    dependencySettings?: string;
    isMulti?: boolean;
    isSearchable?: boolean;
    options?: Array<{ value: string; label: string }>;
    noticeType?: string;
    noIcon?: boolean;
}

interface ConfigurationTab {
    name: string;
    fields: ConfigField[];
}

interface Notice {
    title: string;
    content: string;
    type?: string;
    icon?: boolean;
    close?: boolean;
}

interface InfoModal {
    title: string;
    content: string;
}

interface MenuItem {
    label: string;
    action: string;
    title?: string;
}

type LanguageModalAction = 'syncToCrowdin' | 'updateCrowdinTargetLanguages';

interface LanguageModalState {
    languages: any[];
    action: LanguageModalAction;
}

type PermissionStatus = 'granted' | 'willBeGranted' | 'willBeRegistered' | 'actionRequired';

interface PermissionRowState {
    status: PermissionStatus;
    users: string[];
    description?: string;
    isWarning?: boolean;
}

interface PermissionSummaryState {
    organization?: PermissionRowState;
    project: PermissionRowState;
    applicationSettings: PermissionRowState;
    applicationCredentials: PermissionRowState;
}

type ScheduleModalType = 'crowdin' | 'integration' | null;

const EMPTY_PERMISSION_VALUE = '\u2015';

interface ScheduleModalState {
    type: ScheduleModalType;
    showNewFiles: boolean;
    showAllFolderFiles: boolean;
    newFiles: boolean;
    newFilesClearSelection: boolean;
    selectedFiles: boolean;
    allFolderFiles: boolean;
}

interface MainPageProps {
    name: string;
    userId?: string;
    notice?: Notice;
    checkSubscription?: boolean;
    isManager?: boolean;
    isOwner?: boolean;
    infoModal?: InfoModal;
    configurationFields?: ConfigurationTab[];
    config?: string; // JSON string
    syncNewElements?: {
        crowdin?: boolean;
        integration?: boolean;
    };
    withCronSync?: {
        crowdin?: boolean;
        integration?: boolean;
    };
    webhooks?: {
        crowdin?: boolean;
        integration?: boolean;
    };
    integrationOneLevelFetching?: boolean;
    integrationSearchListener?: boolean;
    integrationPagination?: boolean;
    uploadTranslations?: boolean;
    excludedTargetLanguages?: boolean;
    forcePushSources?: boolean;
    forcePushTranslations?: boolean;
    filtering?: {
        crowdinLanguages?: boolean;
        integrationFilterConfig?: string;
    };
    hasOrganization?: boolean;
    zenModeUrl?: string;
    asyncProgress?: {
        checkInterval: number;
    };
    reloadOnConfigSave?: boolean;
    progressiveCrowdinFilesLoading?: boolean;
    integrationButtonMenuItems?: MenuItem[];
    crowdinButtonMenuItems?: MenuItem[];
}

const getDefaultScheduleModalState = (): ScheduleModalState => ({
    type: null,
    showNewFiles: true,
    showAllFolderFiles: false,
    newFiles: false,
    newFilesClearSelection: false,
    selectedFiles: true,
    allFolderFiles: false,
});

const renderConfigField = (
    field: ConfigField,
    settingsFieldChange: (e: any) => void,
    registerSettingsField: (key?: string) => (element: any) => void,
    suffix: string = 'settings',
) => {
    if (!field.key) {
        return field.labelHtml ? (
            <crowdin-p
                {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                dangerouslySetInnerHTML={{ __html: field.labelHtml }}
            />
        ) : (
            <crowdin-p {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}>
                {field.label}
            </crowdin-p>
        );
    }

    const commonProps = {
        id: `${field.key}-${suffix}`,
        _key: field.key,
        label: field.label,
        ...(field.helpText ? { 'help-text': field.helpText } : {}),
        ...(field.helpTextHtml ? { 'help-text-html': field.helpTextHtml } : {}),
        ...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {}),
    };

    switch (field.type) {
        case 'checkbox':
            return (
                <crowdin-checkbox
                    ref={registerSettingsField(field.key)}
                    {...commonProps}
                    value="false"
                    use-switch
                    {...(field.defaultValue === true ? { checked: String(field.defaultValue) } : {})}
                    onChange={settingsFieldChange}
                />
            );

        case 'select':
            return (
                <crowdin-select
                    ref={registerSettingsField(field.key)}
                    {...commonProps}
                    {...(field.isMulti ? { 'is-multi': true, 'close-on-select': 'false' } : {})}
                    {...(field.isSearchable ? { 'is-searchable': true } : {})}
                    is-position-fixed
                    onChange={settingsFieldChange}
                >
                    {field.options?.map((option) => {
                        const isSelected = Array.isArray(field.defaultValue)
                            ? field.defaultValue.includes(option.value)
                            : field.defaultValue === option.value;
                        return (
                            <option key={option.value} value={option.value} {...(isSelected ? { selected: true } : {})}>
                                {option.label}
                            </option>
                        );
                    })}
                </crowdin-select>
            );

        case 'text':
            return (
                <crowdin-input
                    ref={registerSettingsField(field.key)}
                    {...commonProps}
                    with-fixed-height
                    value={field.defaultValue || ''}
                    onChange={settingsFieldChange}
                />
            );

        case 'textarea':
            return (
                <crowdin-textarea
                    ref={registerSettingsField(field.key)}
                    {...commonProps}
                    value={field.defaultValue || ''}
                    onChange={settingsFieldChange}
                />
            );

        case 'notice':
            return (
                <crowdin-alert
                    {...(field.noticeType ? { type: field.noticeType } : {})}
                    {...(field.label ? { title: field.label } : {})}
                    {...(field.noIcon ? { 'no-icon': String(field.noIcon) } : {})}
                    {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                    dangerouslySetInnerHTML={{ __html: field.helpTextHtml || field.helpText || '' }}
                />
            );

        default:
            return null;
    }
};

export const MainPage: FC<MainPageProps> = (props) => {
    const {
        name,
        userId,
        notice,
        checkSubscription,
        isManager,
        isOwner,
        infoModal,
        configurationFields,
        config: configString,
        syncNewElements,
        withCronSync,
        webhooks,
        integrationOneLevelFetching,
        integrationSearchListener,
        integrationPagination,
        excludedTargetLanguages,
        filtering,
        hasOrganization,
        zenModeUrl,
        asyncProgress = { checkInterval: 3000 },
        reloadOnConfigSave,
        progressiveCrowdinFilesLoading,
        integrationButtonMenuItems,
        crowdinButtonMenuItems,
    } = props;

    const config = useRef<any>(configString ? JSON.parse(configString.replace(/\\n/g, '\\\\n')) : {});

    const folderType = '0';
    const fileType = '1';
    const branchType = '2';

    const JOB_TYPE = {
        updateCrowdin: 'updateCrowdin',
        updateIntegration: 'updateIntegration',
        updateTargetLanguages: 'updateTargetLanguages',
        integrationSyncSettingsSave: 'integrationSyncSettingsSave',
        crowdinSyncSettingsSave: 'crowdinSyncSettingsSave',
        integrationSettingsSave: 'integrationSettingsSave',
    };

    const JOB_STATUS = {
        created: 'created',
        inProgress: 'inProgress',
        failed: 'failed',
        canceled: 'canceled',
        finished: 'finished',
    };

    const silentJobs = [JOB_TYPE.integrationSettingsSave];

    const appComponent = useRef<any>(null);
    const asyncProgressRef = useRef<any>(null);
    const infoModalRef = useRef<any>(null);
    const subscriptionModal = useRef<any>(null);
    const settingsModalRef = useRef<any>(null);
    const scheduleModalRef = useRef<any>(null);
    const selectedFilesCheckboxRef = useRef<any>(null);
    const allFolderFilesCheckboxRef = useRef<any>(null);
    const newFilesCheckboxRef = useRef<any>(null);
    const languageModal = useRef<any>(null);
    const languageSelectRef = useRef<any>(null);
    const permissionsModalRef = useRef<any>(null);
    const userErrorsTableRef = useRef<any>(null);
    const userErrorDetailModalRef = useRef<any>(null);
    const usersSelectRef = useRef<any>(null);
    const settingsFieldRefs = useRef<Record<string, any>>({});
    const refreshDependencies = useFieldDependencies(configurationFields);

    const crowdinFilesToUpdateTargetLanguages = useRef<any>([]);
    const asyncJobs = useRef<any>({});
    const project = useRef<any>({});
    const crowdinData = useRef<any[]>([]);
    const fileToSync = useRef<any[]>([]);
    const syncData = useRef<any>(null);
    const subscriptionLink = useRef<string>('');
    const hasLoadMoreElements = useRef<boolean>(false);
    const savedIntegrationData = useRef<any>(null);
    const currentOpenedFolders = useRef<any>(null);
    const [modalLoaders, setModalLoaders] = useState<Record<string, boolean>>({});
    const [languageModalState, setLanguageModalState] = useState<LanguageModalState | null>(null);
    const [permissionUserOptions, setPermissionUserOptions] = useState<Array<{ id: string; label: string }>>([]);
    const [permissionsSelectedValue, setPermissionsSelectedValue] = useState('[]');
    const [permissionsSummary, setPermissionsSummary] = useState<PermissionSummaryState | null>(null);
    const [isPermissionsConfirmVisible, setIsPermissionsConfirmVisible] = useState(false);
    const [isNoticeVisible, setIsNoticeVisible] = useState(false);
    const [isSettingsSaveDisabled, setIsSettingsSaveDisabled] = useState(false);
    const [scheduleModalState, setScheduleModalState] = useState<ScheduleModalState>(getDefaultScheduleModalState);
    const [isScheduleSaveDisabled, setIsScheduleSaveDisabled] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState<{ daysLeft: number; subscribeLink: string } | null>(null);
    const [isErrorLogsVisible, setIsErrorLogsVisible] = useState(false);
    const [selectedUserError, setSelectedUserError] = useState<any>(null);

    useEffect(() => {
        // Event listeners

        const listeners: Record<string, (e: any) => void> = {
            refreshFilesList: (e: any) => {
                if (e.detail.refreshIntegration) {
                    getIntegrationData({ hardReload: true });
                } else if (e.detail.refreshCrowdin) {
                    getCrowdinData();
                }
            },
            crowdinFilesFolderToggled: (event: any) => {
                if (event.detail.componentId === 'crowdin-files' && event.detail.isOpen && event.detail.type === '1') {
                    getFileProgress(event.detail.id);
                }

                if (integrationOneLevelFetching) {
                    if (event.detail.componentId === 'integration-files' && event.detail.isOpen) {
                        getIntegrationData({ parentId: event.detail.id, recursion: event.detail.recursion });
                    }
                }
            },
            uploadFilesToCrowdin: uploadFilesToCrowdin,
            uploadFilesToIntegration: uploadFilesToIntegration,
            cancelAsyncAction: (e: any) => {
                cancelJob(e.detail);
            },
            keydown: (event: any) => {
                if (event.keyCode === 27) {
                    closeModal(permissionsModalRef.current);
                    closeModal(infoModalRef.current);
                    closeModal(settingsModalRef.current);
                    closeModal(scheduleModalRef.current);
                    if (languageModal.current) {
                        closeModal(languageModal.current);
                    }
                }
            },
            loadMoreClick: async (e: any) => {
                const loadMoreFile = e.detail;
                const fileComponent = appComponent.current?.querySelector('#integration-files') as any;

                if (!fileComponent) {
                    return;
                }

                await fileComponent.setLoadMoreLoading(loadMoreFile.id, true);

                getIntegrationData({ parentId: e.detail.parent_id, paginationData: e.detail.pagination }).finally(
                    async () => {
                        await fileComponent.setLoadMoreLoading(loadMoreFile.id, false);
                    },
                );
            },
        };

        if (integrationSearchListener) {
            listeners['integrationFilterChange'] = async (event: any) => {
                const isEmptySearch = !event.detail || event.detail.trim() === '';

                if (isEmptySearch && savedIntegrationData.current && hasLoadMoreElements.current) {
                    appComponent.current.setIntegrationFilesData(savedIntegrationData.current);
                    appComponent.current.setIntegrationOpenedFolders(currentOpenedFolders.current);
                    return;
                }

                if (!isEmptySearch && hasLoadMoreElements.current) {
                    savedIntegrationData.current = await appComponent.current.getIntegrationFilesData();
                    currentOpenedFolders.current = await appComponent.current.getIntegrationOpenedFolders();
                }

                getIntegrationData({ search: event.detail });
            };
        }

        if (integrationPagination) {
            listeners['fileListEnd'] = (e: any) => {
                getIntegrationData({ page: e.detail.page });
            };
        }

        if (withCronSync || webhooks) {
            listeners['integrationScheduleSync'] = setIntegrationScheduleSync;
            listeners['crowdinScheduleSync'] = setCrowdinScheduleSync;
            listeners['crowdinDisableSync'] = disableCrowdinSync;
            listeners['integrationDisableSync'] = disableIntegrationSync;
        }

        for (const [event, handler] of Object.entries(listeners)) {
            document.body.addEventListener(event, handler);
        }

        // Initial data fetch

        getCrowdinData();
        getIntegrationData({});
        getActiveJobs();

        if (checkSubscription) {
            getSubscriptionInfo();
        }

        setIsNoticeVisible(Boolean(notice) && localStorage.getItem('revised_notice') !== '1');

        return () => {
            for (const [event, handler] of Object.entries(listeners)) {
                document.body.removeEventListener(event, handler);
            }
        };
    }, []);

    useEffect(() => {
        if (!languageModalState || !languageSelectRef.current?.setValue) {
            return;
        }

        languageSelectRef.current.setValue(languageModalState.languages.map((language) => language.id));
    }, [languageModalState]);

    useEffect(() => {
        if (usersSelectRef.current) {
            usersSelectRef.current.value = permissionsSelectedValue;
        }
    }, [permissionUserOptions, permissionsSelectedValue]);

    useEffect(() => {
        syncScheduleCheckbox(selectedFilesCheckboxRef.current, {
            checked: scheduleModalState.selectedFiles,
        });
        syncScheduleCheckbox(allFolderFilesCheckboxRef.current, {
            checked: scheduleModalState.allFolderFiles,
            hidden: !scheduleModalState.showAllFolderFiles,
        });
        syncScheduleCheckbox(newFilesCheckboxRef.current, {
            checked: scheduleModalState.newFiles,
            hidden: !scheduleModalState.showNewFiles,
            clearSelection: scheduleModalState.newFilesClearSelection,
        });
    }, [scheduleModalState]);

    // Expose centralized API to global scope for main.js compatibility
    useEffect(() => {
        (window as any).app = {
            subscriptionModal: subscriptionModal,
            subscriptionLink: subscriptionLink,
            openModal: openModal,
        };

        return () => {
            delete (window as any).app;
        };
    }, []);

    // crowdin-users-select doesn't trigger onChange reliably, using native event listener instead
    useEffect(() => {
        const selectElement = usersSelectRef.current;
        if (selectElement) {
            const handleChange = () => {
                checkUsers();
            };

            selectElement.addEventListener('change', handleChange);

            return () => {
                selectElement.removeEventListener('change', handleChange);
            };
        }
    }, []);

    function integrationLogout() {
        checkOrigin()
            .then((queryParams) => fetch(`api/logout${queryParams}`, { method: 'POST' }))
            .then(checkResponse)
            .then(reloadLocation)
            .then(() => localStorage.removeItem(`revised_${name}`))
            .catch((e) => catchRejection(e, e.error || 'Looks like you are not logged in'));
    }

    function transformCrowdinItemToTree(e: any) {
        const item: any = {
            parent_id: e.parentId ? e.parentId : '0',
            name: e.name,
            id: e.id,
            customContent: e.customContent,
            labels: e.labels,
            disabled: e.disabled,
            tooltip: e.tooltip,
        };
        if (e.type) {
            item.type = e.type;
            item.node_type = fileType;
            item.failed = e.failed;
            item.excludedTargetLanguages = e.excludedTargetLanguages;
        } else {
            item.node_type = e.nodeType || folderType;
        }
        return item;
    }

    function getCrowdinData() {
        appComponent.current?.setAttribute('is-crowdin-loading', 'true');

        let promise;
        if (progressiveCrowdinFilesLoading) {
            promise = checkOrigin()
                .then((restParams) => {
                    const dirPromise = fetch('api/crowdin/files' + restParams + '&mode=directories')
                        .then(checkResponse)
                        .then((directories) => {
                            const dirTree = directories.map(transformCrowdinItemToTree);
                            crowdinData.current = dirTree;
                            appComponent.current?.setCrowdinFilesData(dirTree);
                            return dirTree;
                        });

                    const filesPromise = fetch('api/crowdin/files' + restParams + '&mode=files').then(checkResponse);

                    const projectPromise = fetch('api/crowdin/project' + restParams).then(checkResponse);

                    return Promise.all([dirPromise, filesPromise, projectPromise]);
                })
                .then(([dirTree, files, projectData]) => {
                    const fileTree = files.map(transformCrowdinItemToTree);
                    crowdinData.current = [...dirTree, ...fileTree];
                    appComponent.current?.setCrowdinFilesData(crowdinData.current);

                    project.current = projectData;
                    const languagesSorted = project.current.targetLanguages
                        .map((language: any) => ({
                            sourceLanguageId: projectData.sourceLanguage.editorCode,
                            projectEditorLink: projectData.projectEditorLink,
                            ...language,
                        }))
                        .sort((a: any, b: any) => a.name.localeCompare(b.name));

                    if (project.current.inContext && config.current?.inContext) {
                        languagesSorted.push({ ...project.current.inContextPseudoLanguage, inContext: true });
                    }

                    setLanguagesForTranslation(languagesSorted);
                    appComponent.current?.setCrowdinLanguagesData(languagesSorted);
                });
        } else {
            promise = checkOrigin()
                .then((restParams) => {
                    const filesPromise = fetch('api/crowdin/files' + restParams).then(checkResponse);

                    const projectPromise = fetch('api/crowdin/project' + restParams).then(checkResponse);

                    return Promise.all([filesPromise, projectPromise]);
                })
                .then(([files, projectData]) => {
                    const tree = files.map(transformCrowdinItemToTree);
                    crowdinData.current = tree;
                    appComponent.current.setCrowdinFilesData(tree);

                    project.current = projectData;
                    const languagesSorted = project.current.targetLanguages
                        .map((language: any) => ({
                            sourceLanguageId: projectData.sourceLanguage.editorCode,
                            projectEditorLink: projectData.projectEditorLink,
                            ...language,
                        }))
                        .sort((a: any, b: any) => a.name.localeCompare(b.name));

                    if (project.current.inContext && config.current?.inContext) {
                        languagesSorted.push({ ...project.current.inContextPseudoLanguage, inContext: true });
                    }

                    setLanguagesForTranslation(languagesSorted);
                    appComponent.current.setCrowdinLanguagesData(languagesSorted);
                });
        }

        promise
            .then(() => {
                if (withCronSync || webhooks) {
                    return getSyncSettings('crowdin');
                }
            })
            .catch((e) => catchRejection(e, "Can't fetch Crowdin data"))
            .finally(() => appComponent.current.setAttribute('is-crowdin-loading', false));
    }

    function getIntegrationData(
        { hardReload = false, parentId = '', search = '', page = 0, recursion = false, paginationData } = {} as any,
        recursionState: any = null,
    ) {
        if (!paginationData) {
            appComponent.current.setAttribute('is-integration-loading', true);
        }

        let recursionStateLocal: any = recursionState;

        return checkOrigin()
            .then((restParams) => {
                let url = `api/integration/data${restParams}&parent_id=${encodeURIComponent(parentId)}&search=${encodeURIComponent(search)}&page=${page}`;
                if (paginationData) {
                    url += `&paginationData=${encodeURIComponent(JSON.stringify(paginationData))}`;
                }
                return fetch(url);
            })
            .then(checkResponse)
            .then(async (res) => {
                const files = res.data;
                const stopPagination = res.stopPagination;
                const tree = files.map((e: any) => {
                    const item = {
                        parent_id: e.parentId ? e.parentId : '0',
                        name: e.name,
                        id: e.id,
                        customContent: e.customContent,
                        labels: e.labels,
                        disabled: e.disabled,
                        tooltip: e.tooltip,
                        pagination: e.pagination,
                    } as any;
                    if (e.type) {
                        item.isNew = e.isNew;
                        item.isUpdated = e.isUpdated;
                        item.notSynced = e.notSynced;
                        item.synced = e.synced;
                        item.type = e.type;
                        item.node_type = fileType;
                    } else {
                        item.node_type = e.nodeType || folderType;
                    }
                    return item;
                });

                if ((!paginationData && !parentId && !search) || hardReload) {
                    hasLoadMoreElements.current = tree.some((item: any) => item.node_type === 'load-more');
                }

                const appIntegrationFiles = appComponent.current.querySelector('#integration-files');
                if (hardReload) {
                    appComponent.current.setIntegrationFilesData(tree);
                } else if (tree.length) {
                    appComponent.current.pushIntegrationFilesData(tree).then(() => {
                        if (parentId) {
                            appIntegrationFiles.getSelected().then(async (selection: any) => {
                                const selectedIds = selection
                                    ?.filter((node: any) => node)
                                    .map(({ id }: any) => id.toString());
                                const filteredNodes = tree.filter(
                                    (node: any) =>
                                        !selectedIds.includes(node.id.toString()) &&
                                        selectedIds.includes(node.parent_id.toString()),
                                );

                                if (filteredNodes?.length) {
                                    const filteredNodesId = filteredNodes.map(({ id }: any) => id.toString());
                                    await appIntegrationFiles.setSelected(filteredNodesId);
                                }
                            });
                        }
                    });
                }

                if (integrationPagination) {
                    if (stopPagination) {
                        appIntegrationFiles.setAttribute('load-more-disabled', true);
                    } else {
                        appIntegrationFiles.setAttribute('load-more-disabled', false);
                    }
                }

                if (search) {
                    const openIds = files.filter((e: any) => !e.type).map((e: any) => e.id);
                    appComponent.current.setIntegrationOpenedFolders(openIds);
                }

                if (recursion) {
                    if (!recursionStateLocal) {
                        recursionStateLocal = {
                            openedFolders: new Set(),
                            calls: 0,
                            setLoading(isLoading: boolean) {
                                appComponent.current.setAttribute('is-integration-loading', isLoading);
                            },
                        };
                        recursionStateLocal.setLoading(true);
                    }

                    if (parentId) {
                        recursionStateLocal.openedFolders.add(parentId);
                    }

                    const folderItems = files.filter(isFolder);

                    if (folderItems.length === 0) {
                        return Promise.resolve();
                    }

                    folderItems.forEach((folder: any) => recursionStateLocal.openedFolders.add(folder.id));

                    const currentOpenedFolders = await appComponent.current.getIntegrationOpenedFolders();
                    appComponent.current.setIntegrationOpenedFolders([
                        ...currentOpenedFolders,
                        ...recursionStateLocal.openedFolders,
                    ]);

                    const recursivePromises = folderItems.map(async (folder: any) => {
                        recursionStateLocal.calls++;
                        try {
                            return getIntegrationData({ parentId: folder.id, recursion: true }, recursionState);
                        } finally {
                            recursionStateLocal.calls--;
                            if (recursionStateLocal.calls === 0) {
                                recursionStateLocal.setLoading(false);
                            }
                        }
                    });

                    return Promise.all(recursivePromises);
                }
            })
            .then(() => {
                if (withCronSync || webhooks) {
                    getSyncSettings('integration');
                }
            })
            .catch((e) => catchRejection(e, `Can't fetch ${name} templates`))
            .finally(() => {
                if (!recursionStateLocal && !recursion) {
                    appComponent.current.setAttribute('is-integration-loading', false);
                } else if (recursionStateLocal && recursionStateLocal.calls === 0) {
                    appComponent.current.setAttribute('is-integration-loading', false);
                }
            });
    }

    function isScheduleDisabled(currentConfig = config.current): boolean {
        return String(currentConfig?.schedule ?? '0') === '0';
    }

    async function applyAutoSyncPausedMarkers(provider: string, syncSettingsFiles: any[] = []) {
        const isDisabled = isScheduleDisabled();
        const configuredIds = new Set(
            (Array.isArray(syncSettingsFiles) ? syncSettingsFiles : []).map((item: any) => `${item.id}`),
        );

        const getChangedItems = (tree: any[] = []) =>
            tree
                .map((item: any) => {
                    const nextScheduleDisabled =
                        Boolean(item.schedule) && isDisabled && configuredIds.has(`${item.id}`);
                    const currentScheduleDisabled = Boolean(item.scheduleDisabled);

                    if (currentScheduleDisabled === nextScheduleDisabled) {
                        return null;
                    }

                    return {
                        ...item,
                        scheduleDisabled: nextScheduleDisabled,
                    };
                })
                .filter(Boolean);

        if (provider === 'crowdin') {
            const changedItems = getChangedItems(crowdinData.current || []);
            if (changedItems.length) {
                const changedById = new Map(changedItems.map((item: any) => [`${item.id}`, item]));
                crowdinData.current = (crowdinData.current || []).map(
                    (item: any) => changedById.get(`${item.id}`) || item,
                );
                appComponent.current?.updateCrowdinFileData(changedItems);
            }
            return;
        }

        const integrationData = await appComponent.current?.getIntegrationFilesData?.();
        if (Array.isArray(integrationData)) {
            const changedItems = getChangedItems(integrationData);
            if (changedItems.length) {
                appComponent.current?.updateIntegrationFileData(changedItems);
            }
        }
    }

    async function fetchSyncSettingsFiles(provider: string): Promise<any[]> {
        const restParams = await checkOrigin();
        const res = await fetch(`/api/sync-settings/${provider}` + restParams).then(checkResponse);
        return Array.isArray(res) ? res : [];
    }

    async function refreshAutoSyncPausedIndicatorsAndWarning() {
        const [crowdinSyncSettings, integrationSyncSettings] = await Promise.all([
            fetchSyncSettingsFiles('crowdin'),
            fetchSyncSettingsFiles('integration'),
        ]);

        await applyAutoSyncPausedMarkers('crowdin', crowdinSyncSettings);
        await applyAutoSyncPausedMarkers('integration', integrationSyncSettings);

        if (isScheduleDisabled() && (crowdinSyncSettings.length || integrationSyncSettings.length)) {
            showToast('Auto Sync is configured for some files, but sync schedule is disabled in app settings.');
        }
    }

    function getSyncSettings(provider: string) {
        checkOrigin()
            .then((restParams) => fetch(`/api/sync-settings/${provider}` + restParams))
            .then(checkResponse)
            .then(async (res) => {
                if (provider === 'crowdin') {
                    if (syncNewElements?.crowdin) {
                        appComponent.current.setCrowdinScheduleSync(res, true, true);
                    } else {
                        appComponent.current.setCrowdinScheduleSync(res);
                    }
                } else {
                    if (syncNewElements?.integration) {
                        appComponent.current.setIntegrationScheduleSync(res, true, true);
                    } else {
                        appComponent.current.setIntegrationScheduleSync(res);
                    }
                }
                await applyAutoSyncPausedMarkers(provider, res);
            })
            .catch((e) => catchRejection(e, "Can't fetch file progress"));
    }

    function getFileProgress(fileId: string) {
        checkOrigin()
            .then((restParams) => fetch(`api/crowdin/file-progress/${fileId}` + restParams))
            .then(checkResponse)
            .then((res) => appComponent.current.addCrowdinFileProgress(res))
            .catch((e) => catchRejection(e, "Can't fetch file progress"));
    }

    function uploadFilesToCrowdin(event: any, languages = []) {
        let files = [];
        let uploadTranslations = false;
        let forcePushSources = false;
        if (event.detail?.action === 'uploadTranslations') {
            files = event.detail.files;
            uploadTranslations = true;
            forcePushSources = true;
        } else if (event.detail?.action === 'excludedTargetLanguages') {
            fileToSync.current = event.detail.files;
            openLanguageModal();
            return;
        } else if (event.detail?.action === 'forcePushSources') {
            files = event.detail.files;
            forcePushSources = true;
        } else if (event.detail?.action === 'syncTranslationsToIntegration') {
            syncTranslationsToIntegration(event.detail.files);
            return;
        } else {
            files = event.detail;
        }

        if (languages.length > 0) {
            forcePushSources = true;
        }

        if (event.detail?.length === 0 || !event.detail) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }
        appComponent.current.setAttribute('is-to-crowdin-process', true);
        let req: any;

        if (integrationOneLevelFetching) {
            req = files.map((f: any) => ({
                name: f.name,
                id: f.id,
                type: f.type,
                parentId: f.parent_id,
                nodeType: f.node_type,
                isUpdated: f.isUpdated,
            }));
        } else {
            req = files
                .filter((f: any) => f.node_type !== folderType)
                .map((f: any) => ({
                    name: f.name,
                    id: f.id,
                    type: f.type,
                    parentId: f.parent_id,
                    isUpdated: f.isUpdated,
                }));
        }

        checkOrigin()
            .then((restParams) =>
                fetch(
                    `api/crowdin/update${restParams}&uploadTranslations=${uploadTranslations}&languages=${languages}&forcePushSources=${forcePushSources}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(req),
                    },
                ),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateCrowdin,
                });
            })
            .catch((e) => catchRejection(e, "Can't upload templates to Crowdin"));
    }

    function getActiveJobs() {
        checkOrigin()
            .then((restParams) => fetch(`api/jobs${restParams}`))
            .then(checkResponse)
            .then((jobs) => {
                if (!Array.isArray(jobs)) {
                    return;
                }

                jobs.forEach((job) => {
                    switch (job.type) {
                        case JOB_TYPE.updateCrowdin:
                        case JOB_TYPE.updateTargetLanguages:
                            appComponent.current.setAttribute('is-to-crowdin-process', true);
                            break;
                        case JOB_TYPE.updateIntegration:
                            appComponent.current.setAttribute('is-to-integration-process', true);
                            break;
                        case JOB_TYPE.crowdinSyncSettingsSave:
                            appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);
                            break;
                        case JOB_TYPE.integrationSyncSettingsSave:
                            appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);
                            break;
                        default:
                    }

                    checkJob({
                        jobId: job.id,
                        jobType: job.type,
                    });
                });
            })
            .catch((e) => catchRejection(e, 'Sync status check failed'));
    }

    function cancelJob(jobId: string) {
        if (asyncJobs.current[jobId]?.isFailed) {
            return;
        }

        checkOrigin()
            .then((restParams) =>
                fetch('api/jobs' + restParams + '&jobId=' + jobId, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                }),
            )
            .then(checkResponse)
            .then(() => showToast('Cancellation…'))
            .catch((e) => catchRejection(e, 'Sync cancellation failed'));
    }

    function checkJob({ jobId, jobType, onSuccess, onError, onFinally }: any) {
        switch (jobType) {
            case JOB_TYPE.updateCrowdin:
            case JOB_TYPE.updateTargetLanguages:
                if (!onFinally) {
                    onFinally = () => {
                        appComponent.current.setAttribute('is-to-crowdin-process', false);
                        getCrowdinData();
                    };
                }
                break;
            case JOB_TYPE.updateIntegration:
                if (!onFinally) {
                    onFinally = () => appComponent.current.setAttribute('is-to-integration-process', false);
                }
                break;
            case JOB_TYPE.crowdinSyncSettingsSave:
                if (!onFinally) {
                    onFinally = () => appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', false);
                }
                break;
            case JOB_TYPE.integrationSyncSettingsSave:
                if (!onFinally) {
                    onFinally = () =>
                        appComponent.current.setAttribute('is-integration-sync-settings-in-progress', false);
                }
                break;
            default:
        }

        checkOrigin()
            .then((restParams) => fetch('api/jobs' + restParams + '&jobId=' + jobId))
            .then(checkResponse)
            .then((job) => {
                const isFailed = [JOB_STATUS.failed, JOB_STATUS.canceled].includes(job.status);
                const progress = isFailed || JOB_STATUS.finished === job.status ? 100 : job.progress;
                const info = JOB_STATUS.canceled === job.status ? `Cancelled\n${job.info || ''}` : job.info;

                if (!silentJobs.find((type) => type === jobType)) {
                    pushJobs([
                        {
                            id: job.id,
                            title: job.title,
                            progress,
                            info,
                            isFailed,
                        },
                    ]);
                }

                if (isFailed) {
                    if (onError) {
                        onError();
                    }
                } else if ([JOB_STATUS.created, JOB_STATUS.inProgress].includes(job.status) && job.progress < 100) {
                    setTimeout(() => {
                        checkJob({ jobId, jobType, onSuccess, onError, onFinally });
                    }, asyncProgress.checkInterval);
                    return;
                } else {
                    if (onSuccess) {
                        onSuccess(job);
                    }
                }

                if (onFinally) {
                    onFinally(job);
                }
            })
            .catch((e) => {
                if (onFinally) {
                    onFinally();
                }

                if (!silentJobs.find((type) => type === jobType)) {
                    pushJobs([
                        {
                            id: jobId,
                            isFailed: true,
                        },
                    ]);
                }

                showToast('Sync status check failed');
            });
    }

    function pushJobs(jobs: any[]) {
        const el = asyncProgressRef.current;
        if (el && el.pushJobs) {
            el.pushJobs(jobs);
            jobs.forEach((job) => (asyncJobs.current[job.id] = job));
        } else {
            setTimeout(() => {
                pushJobs(jobs);
            }, 300);
        }
    }

    function uploadFilesToIntegration(event: any) {
        let files = {} as any;
        let forcePushTranslations = false;
        if (event.detail?.action === 'forcePushTranslations') {
            files = event.detail.files;
            forcePushTranslations = true;
        } else if (event.detail?.action === 'excludedTargetLanguages') {
            crowdinFilesToUpdateTargetLanguages.current = event.detail.files;
            openLanguageModalForCrowdinFiles();
            return;
        } else if (event.detail?.action === 'syncSourcesToCrowdin') {
            syncToCrowdinFromCrowdinFiles(event.detail.files, false);
            return;
        } else if (event.detail?.action === 'syncTranslationsToCrowdin') {
            syncToCrowdinFromCrowdinFiles(event.detail.files, true);
            return;
        } else {
            files = event.detail;
        }

        if (Object.keys(event.detail).length === 0 || !event.detail) {
            showToast('Select files which will be uploaded to ${name}');
            return;
        }

        appComponent.current.setAttribute('is-to-integration-process', true);
        const req = {} as any;
        Object.keys(files)
            .filter((id) => crowdinData.current.find((c) => c.id === id).node_type === fileType)
            .forEach((id) => (req[id] = files[id]));
        checkOrigin()
            .then((restParams) =>
                fetch(`api/integration/update${restParams}&forcePushTranslations=${forcePushTranslations}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(req),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateIntegration,
                });
            })
            .catch((e) => catchRejection(e, `Can't upload files to ${name}`));
    }

    function syncToCrowdinFromCrowdinFiles(files: any, uploadTranslations: any) {
        if (!files || Object.keys(files).length === 0) {
            showToast('Select files to sync');
            return;
        }

        appComponent.current.setAttribute('is-to-crowdin-process', true);
        const req = {} as any;
        Object.keys(files)
            .filter((id) => crowdinData.current.find((c) => c.id === id)?.node_type === fileType)
            .forEach((id) => (req[id] = files[id]));

        checkOrigin()
            .then((restParams) =>
                fetch(
                    `api/crowdin/update${restParams}&matchFromCrowdin=true&uploadTranslations=${uploadTranslations}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(req),
                    },
                ),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateCrowdin,
                });
            })
            .catch((e) => catchRejection(e, "Can't sync files to Crowdin"));
    }

    function syncTranslationsToIntegration(files: any[]) {
        if (!files || files.length === 0) {
            showToast('Select files to sync');
            return;
        }
        appComponent.current.setAttribute('is-to-integration-process', true);
        const req = files
            .filter((f) => f.node_type !== folderType)
            .map((f) => ({
                name: f.name,
                id: f.id,
                type: f.type,
                parentId: f.parent_id,
            }));
        checkOrigin()
            .then((restParams) =>
                fetch(`api/integration/update${restParams}&matchFromIntegration=true`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(req),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateIntegration,
                });
            })
            .catch((e) => catchRejection(e, `Can't sync translations to ${name}`));
    }

    function openLanguageModal() {
        setLanguageModalState((state) => ({
            languages: state?.languages ?? project.current.targetLanguages ?? [],
            action: 'syncToCrowdin',
        }));
        openModal(languageModal.current);
    }

    function openLanguageModalForCrowdinFiles() {
        setLanguageModalState((state) => ({
            languages: state?.languages ?? project.current.targetLanguages ?? [],
            action: 'updateCrowdinTargetLanguages',
        }));
        openModal(languageModal.current);
    }

    async function uploadFilesToCrowdinWithExcludedLanguages() {
        const selectedLanguages = await languageSelectRef.current.getValue();
        closeModal(languageModal.current);

        uploadFilesToCrowdin({ detail: fileToSync.current }, selectedLanguages);
    }

    async function updateCrowdinFilesTargetLanguages() {
        const selectedLanguages = await languageSelectRef.current.getValue();
        closeModal(languageModal.current);

        const fileIds = Object.keys(crowdinFilesToUpdateTargetLanguages.current).map((id) => parseInt(id, 10));

        if (fileIds.length === 0) {
            showToast('Select files to update');
            return;
        }

        appComponent.current.setAttribute('is-to-crowdin-process', true);

        checkOrigin()
            .then((restParams) =>
                fetch(`api/crowdin/files-target-languages${restParams}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ fileIds, languages: selectedLanguages }),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateTargetLanguages,
                });
            })
            .catch((e) => catchRejection(e, "Can't update files target languages"));
    }

    function setLanguagesForTranslation(languages: any[], action: LanguageModalAction = 'syncToCrowdin') {
        setLanguageModalState({ languages, action });
    }

    function hideConfirmUsersBlock() {
        setIsPermissionsConfirmVisible(false);

        permissionsModalRef.current?.setAttribute('body-overflow-unset', true);
    }

    function getUserFullName(user: any) {
        if (user.full_name) {
            return user.full_name;
        }

        const ownerFullName = user.name || ((user.first_name || '') + ' ' + (user.last_name || '')).trim();

        return !!ownerFullName && user?.login !== ownerFullName ? `${ownerFullName} (${user?.login})` : user?.login;
    }

    function showPermissionsDialog() {
        hideConfirmUsersBlock();
        openModal(permissionsModalRef.current);
        setLoader('#permissions-modal');
        setPermissionsSelectedValue('[]');

        Promise.all([
            new Promise((resolve) => {
                checkOrigin()
                    .then((restParams) => fetch('api/users' + restParams))
                    .then(checkResponse)
                    .then((res) => {
                        resolve(JSON.stringify(res.data.managers));
                    });
            }),
            new Promise((resolve) => {
                AP.getUsers((res: any) => {
                    const users = Object.values(res).filter((user: any) => user.id !== Number(userId));
                    resolve(users);
                });
            }),
        ])
            .then(([managers, users]) => {
                const parsedManagers = (JSON.parse(managers as string) || []) as string[];
                const availableUsers = users as any[];
                const validUserIds = new Set(availableUsers.map((user: any) => `${user.id}`));
                const filteredManagers = parsedManagers.filter((id: string) => validUserIds.has(id));

                setPermissionUserOptions(
                    availableUsers.map((user: any) => ({
                        id: String(user.id),
                        label: getUserFullName(user),
                    })),
                );
                setPermissionsSelectedValue(JSON.stringify(filteredManagers));
            })
            .catch((e) => catchRejection(e, "Can't fetch users"))
            .finally(() => unsetLoader('#permissions-modal'));
    }

    function showConfirmUsersBlock() {
        setIsPermissionsConfirmVisible(true);

        permissionsModalRef.current?.removeAttribute('body-overflow-unset');
    }

    function checkUsers() {
        setLoader('#permissions-modal');

        const select = usersSelectRef.current as any;

        if (select.value === '[]') {
            hideConfirmUsersBlock();
            unsetLoader('#permissions-modal');
            return;
        }

        const users = JSON.parse(select.value) as any[];
        const user_ids = users.filter((id) => parseInt(id));
        const emails = users.filter((id) => !parseInt(id));

        const params = { user_ids, emails };

        Promise.all([
            new Promise((resolve) => {
                checkOrigin()
                    .then((restParams) => fetch('api/users' + restParams))
                    .then(checkResponse)
                    .then((usersResponse) => {
                        resolve(usersResponse.data.managers);
                    });
            }) as Promise<any>,
            new Promise((resolve) => {
                AP.validateUsers(params, (res) => {
                    resolve(res.data);
                });
            }) as Promise<any>,
            new Promise((resolve) => {
                AP.getUsers((res) => {
                    resolve(res);
                });
            }) as Promise<any>,
        ])
            .then(([appManagers, checkUsersResponse, allUsers]) => {
                const newUserEmails = checkUsersResponse.newUserEmails.map((email: string) => ({ name: email }));

                const newProjectManagers = [
                    ...newUserEmails,
                    ...checkUsersResponse.newProjectManagers.map((user: any) => ({
                        name: getUserFullName(user),
                    })),
                ];

                const newApplicationAccessUsers = [
                    ...newUserEmails,
                    ...checkUsersResponse.newApplicationAccessUsers.map((user: any) => ({
                        name: getUserFullName(user),
                    })),
                ];

                const newApplicationManagers = [
                    ...newUserEmails,
                    ...Object.values(allUsers as any)
                        .filter(
                            (user: any) =>
                                users.some((id) => +id === user.id) && !appManagers.some((id: any) => +id === user.id),
                        )
                        .map((user) => ({ name: getUserFullName(user) })),
                ];

                prepareUsersConfirmBlock({
                    ...checkUsersResponse,
                    newUserEmails,
                    newProjectManagers,
                    newApplicationAccessUsers,
                    newApplicationManagers,
                });
            })
            .catch((e) => catchRejection(e, e?.error || "Can't invite users"))
            .finally(() => unsetLoader('#permissions-modal'));
    }

    function inviteUsers() {
        setLoader('#permissions-modal');

        const select = usersSelectRef.current as any;
        const users = JSON.parse(select.value) as any[];
        const user_ids = users.filter((id) => parseInt(id));
        const emails = users.filter((id) => !parseInt(id));

        const params = { user_ids, emails };

        AP.inviteUsers(params, (res) => {
            if (!res.success) {
                catchRejection(res, res?.error?.message || "Can't invite users");
                unsetLoader('#permissions-modal');
                return;
            }

            checkOrigin()
                .then((restParams) =>
                    fetch('api/invite-users' + restParams, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(res.data),
                    }),
                )
                .then(checkResponse)
                .then((res) => {
                    if (!res.success) {
                        catchRejection(res, res?.message || "Can't invite users");
                        return;
                    }

                    showToast('Users successfully updated');
                    hideConfirmUsersBlock();
                    closeModal(permissionsModalRef.current);
                })
                .catch((e) => catchRejection(e, e?.error || "Can't invite users"))
                .finally(() => unsetLoader('#permissions-modal'));
        });
    }

    function prepareUsersConfirmBlock(usersData: any) {
        showConfirmUsersBlock();

        const toNames = (users: Array<{ name: string }> = []) => users.map((user) => user.name);
        const applicationSettingsDescription = 'This can be configured in organization settings (Apps section).';
        const hasApplicationAccessUsers = usersData.newApplicationAccessUsers.length > 0;

        setPermissionsSummary({
            ...(hasOrganization
                ? {
                      organization: {
                          status: usersData.newUserEmails.length
                              ? usersData.inviteRestricted
                                  ? 'actionRequired'
                                  : 'willBeRegistered'
                              : 'granted',
                          users: toNames(usersData.newUserEmails),
                          description: usersData.inviteRestricted
                              ? 'New user invitations are restricted to administrators.'
                              : '',
                          isWarning: usersData.inviteRestricted,
                      } as PermissionRowState,
                  }
                : {}),
            project: {
                status: usersData.newProjectManagers.length ? 'willBeGranted' : 'granted',
                users: toNames(usersData.newProjectManagers),
            },
            applicationSettings: {
                status: hasApplicationAccessUsers
                    ? usersData.isAdmin
                        ? 'willBeGranted'
                        : 'actionRequired'
                    : 'granted',
                users: toNames(usersData.newApplicationAccessUsers),
                description: hasApplicationAccessUsers
                    ? usersData.isAdmin
                        ? applicationSettingsDescription
                        : `${applicationSettingsDescription} Only organization admins have permission to update this setting.`
                    : applicationSettingsDescription,
                isWarning: hasApplicationAccessUsers && !usersData.isAdmin,
            },
            applicationCredentials: {
                status: usersData.newApplicationManagers.length ? 'willBeGranted' : 'granted',
                users: toNames(usersData.newApplicationManagers),
            },
        });
    }

    function setLoader(id: string) {
        const loaderKey = id.replace(/^#/, '');
        setModalLoaders((currentLoaders) => ({ ...currentLoaders, [loaderKey]: true }));
    }

    function unsetLoader(id: string) {
        const loaderKey = id.replace(/^#/, '');
        setTimeout(function () {
            setModalLoaders((currentLoaders) => ({ ...currentLoaders, [loaderKey]: false }));
        }, 500);
    }

    function triggerEvent(el: any, type: string) {
        const e = document.createEvent('HTMLEvents') as any;
        e.initEvent(type, false, true);
        el.dispatchEvent(e);
    }

    function parseCrowdinSelectValue(value: any): any[] {
        if (Array.isArray(value)) {
            return value;
        }

        if (value === undefined || value === null || value === '') {
            return [];
        }

        try {
            const parsedValue = JSON.parse(value);
            return Array.isArray(parsedValue) ? parsedValue : [parsedValue];
        } catch (e) {
            return [value];
        }
    }

    function registerSettingsField(key?: string) {
        return (element: any) => {
            if (!key) {
                return;
            }

            if (element) {
                settingsFieldRefs.current[key] = element;
            } else {
                delete settingsFieldRefs.current[key];
            }
        };
    }

    function syncScheduleCheckbox(
        checkbox: any,
        {
            checked,
            hidden = false,
            clearSelection = false,
        }: { checked: boolean; hidden?: boolean; clearSelection?: boolean },
    ) {
        if (!checkbox) {
            return;
        }

        checkbox.checked = checked;
        checkbox.value = checked;

        if (hidden) {
            checkbox.setAttribute('hidden', '');
        } else {
            checkbox.removeAttribute('hidden');
        }

        if (clearSelection) {
            checkbox.setAttribute('clear-selection', '');
        } else {
            checkbox.removeAttribute('clear-selection');
        }
    }

    function fillSettingsForm() {
        Object.entries(config.current).forEach(([key, value]) => {
            const el = settingsFieldRefs.current[key];
            if (el && (value || el.tagName.toLowerCase() === 'crowdin-checkbox')) {
                if (el.tagName.toLowerCase() === 'crowdin-select') {
                    if (el.hasAttribute('is-multi')) {
                        el.value = JSON.stringify(value);
                    } else {
                        el.value = JSON.stringify([value]);
                    }
                } else if (el.tagName.toLowerCase() === 'crowdin-checkbox') {
                    el.checked = !!value;
                } else if (el.tagName.toLowerCase() === 'crowdin-textarea') {
                    el.value = value;
                    el.setValue(value);
                } else {
                    el.value = value;
                }

                triggerEvent(el, 'change');
            }
        });

        setTimeout(refreshDependencies, 100);
    }

    function saveSettings() {
        setLoader('#settings-modal');
        const configReq = {} as any;

        configurationFields?.forEach((tab) => {
            tab.fields.forEach((field) => {
                if (!field.key) {
                    return;
                }

                const el = settingsFieldRefs.current[field.key];

                if (!el) {
                    return;
                }

                const tagName = el.tagName.toLowerCase();

                if (tagName === 'crowdin-select') {
                    const value = parseCrowdinSelectValue(el.value);
                    configReq[field.key] = el.hasAttribute('is-multi') ? value : value[0];
                    return;
                }

                if (tagName === 'crowdin-checkbox') {
                    configReq[field.key] = !!el.checked;
                    return;
                }

                configReq[field.key] = el.value;
            });
        });

        let isValidationError = false;
        let isSettingsSaveJobPending = false;
        let isSettingsSaveFailed = false;
        setIsSettingsSaveDisabled(true);
        checkOrigin()
            .then((restParams) =>
                fetch('api/settings' + restParams, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ config: configReq }),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                if (res?.jobId) {
                    isSettingsSaveJobPending = true;
                    checkJob({
                        jobId: res.jobId,
                        jobType: JOB_TYPE.integrationSettingsSave,
                        onSuccess: () => {
                            showToast('Settings successfully saved');
                            config.current = configReq;
                            refreshAutoSyncPausedIndicatorsAndWarning().catch((e) =>
                                catchRejection(e, "Can't fetch sync settings warning"),
                            );
                        },
                        onError: () => {
                            isSettingsSaveFailed = true;
                            showToast("Can't save settings");
                        },
                        onFinally: () => {
                            unsetLoader('#settings-modal');
                            setIsSettingsSaveDisabled(false);
                            if (!isValidationError && !isSettingsSaveFailed) {
                                closeModal(settingsModalRef.current);
                                if (reloadOnConfigSave) {
                                    getIntegrationData({ hardReload: true });
                                    getCrowdinData();
                                }
                            }
                        },
                    });
                    return;
                }

                showToast('Settings successfully saved');
                config.current = configReq;
                refreshAutoSyncPausedIndicatorsAndWarning().catch((e) =>
                    catchRejection(e, "Can't fetch sync settings warning"),
                );
            })
            .catch((e) => {
                if (e?.error && e?.error?.type === 'validation_error') {
                    Object.keys(e?.error?.details).forEach((key) => {
                        const el = settingsFieldRefs.current[key];
                        if (el) {
                            el.setAttribute('error', 'true');
                            el.setAttribute('error-text', e?.error?.details[key]);
                        }
                    });

                    isValidationError = true;
                    showToast(e?.error?.details?.message || "Can't save settings");
                    setIsSettingsSaveDisabled(false);
                    return;
                }
                catchRejection(e, "Can't save settings");
            })
            .finally(() => {
                if (isSettingsSaveJobPending) {
                    return;
                }
                unsetLoader('#settings-modal');
                setIsSettingsSaveDisabled(false);
                if (!isValidationError) {
                    closeModal(settingsModalRef.current);
                    if (reloadOnConfigSave) {
                        getIntegrationData({ hardReload: true });
                        getCrowdinData();
                    }
                }
            });
    }

    function getIntegrationFoldersToExpand(allData: any, syncedFiles: any) {
        return Array.isArray(allData)
            ? allData
                  .filter(
                      (node) =>
                          node.node_type === folderType &&
                          !syncedFiles.find((syncedFile: any) => syncedFile.parent_id === node.id),
                  )
                  .map((node) => ({
                      ...node,
                      nodeType: node.node_type,
                      parentId: node.parent_id,
                  }))
            : [];
    }

    async function saveScheduleSync() {
        const { type, newFiles: newFile, selectedFiles, allFolderFiles } = scheduleModalState;

        if (type === 'crowdin') {
            const crowdinFiles = await appComponent.current.getCrowdinFilesData();
            const syncedFileIds = Object.keys(syncData.current);
            const crowdinFolderIds = crowdinFiles
                .filter((file: any) => file.node_type === folderType || file.node_type === branchType)
                .filter((file: any) => syncedFileIds.includes(String(file.id)))
                .map((file: any) => String(file.id));

            appComponent.current.setCrowdinScheduleSync(syncData.current, newFile, selectedFiles);
            appComponent.current.setCrowdinFilesHasSyncOption(true);
            const syncedFiles = await appComponent.current.getCrowdinScheduleSync(true);
            appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);
            updateSyncSettings(syncedFiles, 'schedule', 'crowdin', [], crowdinFolderIds);
        } else if (type === 'integration') {
            if (syncNewElements?.integration) {
                appComponent.current.setIntegrationScheduleSync(syncData.current, newFile, selectedFiles);
            } else {
                appComponent.current.setIntegrationScheduleSync(syncData.current);
            }

            appComponent.current.setIntegrationFilesHasSyncOption(true);
            const syncedFiles = await appComponent.current.getIntegrationScheduleSync(true);
            appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);

            if (integrationOneLevelFetching) {
                updateSyncSettings(
                    syncedFiles,
                    'schedule',
                    'integration',
                    selectedFiles ? getIntegrationFoldersToExpand(syncData.current, syncedFiles) : [],
                );
            } else {
                if (allFolderFiles) {
                    const integrationExpandedFolders = syncData.current
                        .filter((file: any) => file.node_type === folderType)
                        .map((file: any) => ({
                            ...file,
                            nodeType: file.node_type,
                            parentId: file.parent_id,
                        }));
                    updateSyncSettings(syncedFiles, 'schedule', 'integration', integrationExpandedFolders);
                } else {
                    updateSyncSettings(syncedFiles, 'schedule', 'integration');
                }
            }
        }

        closeModal(scheduleModalRef.current);
    }

    async function openScheduleModal(type: Exclude<ScheduleModalType, null>, options = {} as any) {
        const { showAllFolderFiles = false, showNewFiles = true } = options;
        let newFileStatus = 'unChecked';

        const integrationSyncFolders = ((await appComponent.current?.getIntegrationScheduleSync?.(true)) || []).filter(
            (elements: any) => elements.node_type === '0',
        );

        const integrationFiles = appComponent.current?.querySelector('#integration-files') as any;
        const selectedIntegrationFolders = ((await integrationFiles?.getSelected?.(true)) || []).filter(
            (elements: any) => elements.node_type === '0',
        );

        for (let i = 0; i < selectedIntegrationFolders.length; i++) {
            const selectedElement = selectedIntegrationFolders[i];

            const found = integrationSyncFolders.some(
                (integrationElement: any) => integrationElement.id === selectedElement.id,
            );

            if (found) {
                newFileStatus = 'checked';
            } else if (!found && newFileStatus === 'checked') {
                newFileStatus = 'clear-selection';
                break;
            } else {
                newFileStatus = 'unChecked';
            }
        }

        setScheduleModalState({
            type,
            showAllFolderFiles,
            showNewFiles,
            newFiles: showNewFiles && newFileStatus === 'checked',
            newFilesClearSelection: showNewFiles && newFileStatus === 'clear-selection',
            selectedFiles: true,
            allFolderFiles: false,
        });
        setIsScheduleSaveDisabled(false);
        openModal(scheduleModalRef.current);
    }

    function onChangeAutoSynchronizationOptions() {
        const nextState = {
            newFiles: scheduleModalState.showNewFiles ? !!newFilesCheckboxRef.current?.checked : false,
            selectedFiles: !!selectedFilesCheckboxRef.current?.checked,
            allFolderFiles: scheduleModalState.showAllFolderFiles
                ? !!allFolderFilesCheckboxRef.current?.checked
                : false,
        };

        setScheduleModalState((currentState) => ({
            ...currentState,
            ...nextState,
            newFilesClearSelection: false,
        }));
        setIsScheduleSaveDisabled(!nextState.newFiles && !nextState.selectedFiles && !nextState.allFolderFiles);
    }

    async function hasFolder(selectedFiles: any) {
        let isFolder;
        if (Array.isArray(selectedFiles)) {
            isFolder = selectedFiles.find((file) => file.node_type === folderType || file.node_type === branchType);
        } else {
            const files = await appComponent.current.getCrowdinFilesData();
            const folders = files.filter((file: any) => file.node_type === folderType || file.node_type === branchType);
            isFolder = folders.find((folder: any) => selectedFiles.hasOwnProperty(folder.id));
        }

        return isFolder !== undefined;
    }

    async function setIntegrationScheduleSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        let syncedFiles;

        if (syncNewElements?.integration) {
            const newIntegrationFiles = config.current?.['new-integration-files'];

            syncData.current = e.detail;
            const isFolder = await hasFolder(e.detail);

            const shouldShowModal = isFolder && !newIntegrationFiles;
            const showNewFiles = true; // завжди показуємо New files коли є syncNewElements.integration
            const showAllFolderFiles = hasLoadMoreElements.current; // показуємо All files якщо є hasLoadMoreElements

            if (shouldShowModal) {
                await openScheduleModal('integration', { showNewFiles, showAllFolderFiles });
                return;
            }

            appComponent.current.setIntegrationScheduleSync(e.detail);
            appComponent.current.setIntegrationFilesHasSyncOption(true);
            syncedFiles = await appComponent.current.getIntegrationScheduleSync(true);
        } else {
            syncData.current = e.detail;
            const isFolder = await hasFolder(e.detail);

            if (hasLoadMoreElements.current && isFolder) {
                await openScheduleModal('integration', { showNewFiles: false, showAllFolderFiles: true });
                return;
            }

            syncedFiles = await appComponent.current.getIntegrationScheduleSync();
        }

        appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);

        if (integrationOneLevelFetching) {
            updateSyncSettings(
                syncedFiles,
                'schedule',
                'integration',
                getIntegrationFoldersToExpand(e.detail, syncedFiles),
            );
        } else {
            if (hasLoadMoreElements.current) {
                const integrationExpandedFolders = e.detail
                    .filter((file: any) => file.node_type === folderType)
                    .map((file: any) => ({
                        ...file,
                        nodeType: file.node_type,
                        parentId: file.parent_id,
                    }));

                updateSyncSettings(syncedFiles, 'schedule', 'integration', integrationExpandedFolders);
            } else {
                updateSyncSettings(syncedFiles, 'schedule', 'integration');
            }
        }
    }

    async function setCrowdinScheduleSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        let syncedFiles;

        if (syncNewElements?.crowdin) {
            const newCrowdinFiles = config.current?.['new-crowdin-files'];

            syncData.current = e.detail;
            const isFolder = await hasFolder(e.detail);
            if (isFolder && !newCrowdinFiles) {
                await openScheduleModal('crowdin');
                return;
            }

            appComponent.current.setCrowdinScheduleSync(e.detail);
            appComponent.current.setCrowdinFilesHasSyncOption(true);
            syncedFiles = await appComponent.current.getCrowdinScheduleSync(true);
        } else {
            syncedFiles = await appComponent.current.getCrowdinScheduleSync();
        }

        appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);
        updateSyncSettings(syncedFiles, 'schedule', 'crowdin');
    }

    async function disableIntegrationSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);

        let syncedFiles;
        if (syncNewElements?.integration) {
            syncedFiles = await appComponent.current.getIntegrationScheduleSync(true);
        } else {
            syncedFiles = await appComponent.current.getIntegrationScheduleSync();
        }

        updateSyncSettings(syncedFiles, 'schedule', 'integration');
    }

    async function disableCrowdinSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);

        let syncedFiles;
        if (syncNewElements?.crowdin) {
            syncedFiles = await appComponent.current.getCrowdinScheduleSync(true);
        } else {
            syncedFiles = await appComponent.current.getCrowdinScheduleSync();
        }

        updateSyncSettings(syncedFiles, 'schedule', 'crowdin');
    }

    function updateSyncSettings(
        files: any,
        type: string,
        provider: string,
        expandIntegrationFolders = [] as any[],
        folderIds = [] as string[],
    ) {
        checkOrigin()
            .then((restParams) =>
                fetch('api/sync-settings' + restParams, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        files,
                        type,
                        provider,
                        expandIntegrationFolders,
                        hasLoadMoreElements: hasLoadMoreElements.current,
                        folderIds,
                    }),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: (JOB_TYPE as any)[`${provider}SyncSettingsSave`],
                });
            })
            .catch((e) => catchRejection(e, "Can't save schedule sync settings"));
    }

    function getSubscriptionInfo() {
        checkOrigin()
            .then((restParams) => fetch('api/subscription-info' + restParams))
            .then(checkResponse)
            .then((res) => {
                if (res.showInfo) {
                    subscriptionLink.current = res.subscribeLink;
                    setSubscriptionInfo({ daysLeft: res.daysLeft, subscribeLink: res.subscribeLink });
                }
            });
    }

    function closeAlert(suffix: string) {
        const alertName = suffix ?? name;
        setIsNoticeVisible(false);
        localStorage.setItem(`revised_${alertName}`, '1');
    }

    function showErrorLogs() {
        setIsErrorLogsVisible(true);

        checkOrigin()
            .then((restParams) => fetch('api/user-errors' + restParams))
            .then(checkResponse)
            .then((res) => {
                const table = userErrorsTableRef.current;

                const clickRow = (field: any, index: any, item: any) => {
                    setSelectedUserError(item);
                    openModal(userErrorDetailModalRef.current);
                };

                const formatDate = (field: any, index: any, item: any) => {
                    return item.formattedDate;
                };

                if (table.setTableConfig) {
                    table.setTableConfig({
                        defaultSortingColumn: 'createdAt',
                        defaultSortingDir: 'desc',
                        headerFields: [
                            {
                                field: 'createdAt',
                                name: 'Date',
                                isSortable: true,
                                clickFn: clickRow,
                                formatFn: formatDate,
                            },
                            {
                                field: 'action',
                                name: 'Action',
                                clickFn: clickRow,
                            },
                            {
                                field: 'message',
                                name: 'Message',
                                clickFn: clickRow,
                            },
                        ],
                    });
                }

                table.setTableData(JSON.stringify(res));
                table.setAttribute('is-loading', false);
            })
            .catch((e) => catchRejection(e, "Can't fetch error logs"));
    }

    function showIntegration() {
        setIsErrorLogsVisible(false);
    }

    function isJSON(string: string) {
        try {
            JSON.parse(string);
            return true;
        } catch (e) {
            return false;
        }
    }

    function getUserErrorDetail(error: any): ReactNode {
        const data = JSON.parse(error.data);
        const preClassName = 'block rounded bg-[#eceff1] p-4 text-[87.5%] text-[#212529]';

        const rows: Array<{ label: string; value: ReactNode }> = [
            { label: 'Action', value: error.action },
            {
                label: 'Message',
                value: isJSON(error.message) ? <pre className={preClassName}>{error.message}</pre> : error.message,
            },
            { label: 'Date/time', value: error.formattedDate },
        ];

        if (data.requestParams) {
            rows.push({ label: 'Method', value: data.requestParams.method });
        }

        if (data.responseData) {
            rows.push({
                label: 'Response Data',
                value: <pre className={preClassName}>{JSON.stringify(data.responseData, null, 2)}</pre>,
            });
        }

        if (data.appData) {
            if (Array.isArray(data.appData)) {
                rows.push({
                    label: 'App Data',
                    value: <pre className={preClassName}>{JSON.stringify(data.appData, null, 2)}</pre>,
                });
            } else if (typeof data.appData === 'object') {
                for (const key in data.appData) {
                    rows.push({
                        label: key.charAt(0).toUpperCase() + key.slice(1),
                        value: <pre className={preClassName}>{JSON.stringify(data.appData[key], null, 2)}</pre>,
                    });
                }
            } else {
                rows.push({ label: 'App Data', value: data.appData });
            }
        }

        return (
            <div className="-m-6 h-auto max-h-[579px] overflow-auto">
                <table className="w-full border-separate border-spacing-0">
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.label}>
                                <td className="min-w-[200px] p-2 px-6 align-top font-medium">{row.label}</td>
                                <td className="break-all bg-[var(--crowdin-primary-blue-gray,#f6f6f6)] p-2 px-6 text-[var(--crowdin-body-color,rgba(38,50,56,0.87))]">
                                    {row.value}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderPermissionStatus(status: PermissionStatus): ReactNode {
        switch (status) {
            case 'willBeGranted':
                return (
                    <span className="inline-block cursor-default rounded-[15px] bg-[var(--crowdin-info)] px-3 py-1 text-center text-xs font-medium text-[var(--crowdin-white)]">
                        Will Be Granted
                    </span>
                );
            case 'willBeRegistered':
                return (
                    <span className="inline-block cursor-default rounded-[15px] bg-[var(--crowdin-info)] px-3 py-1 text-center text-xs font-medium text-[var(--crowdin-white)]">
                        Will Be Registered
                    </span>
                );
            case 'actionRequired':
                return (
                    <span className="inline-block cursor-default rounded-[15px] bg-[var(--crowdin-warning-bg)] px-3 py-1 text-center text-xs font-medium text-[var(--crowdin-warning)]">
                        Action Required
                    </span>
                );
            case 'granted':
            default:
                return <crowdin-p>{EMPTY_PERMISSION_VALUE}</crowdin-p>;
        }
    }

    function renderPermissionUsers(row?: PermissionRowState): ReactNode {
        if (!row || row.users.length === 0) {
            return row ? renderPermissionStatus(row.status) : null;
        }

        return (
            <ul className="m-0 list-none p-0">
                {row.users.map((user) => (
                    <li key={user} className="mb-1 text-sm last:mb-0">
                        <crowdin-p>{user}</crowdin-p>
                    </li>
                ))}
            </ul>
        );
    }

    function renderPermissionRowDescription(row?: PermissionRowState): ReactNode {
        if (!row?.description) {
            return null;
        }

        return (
            <div
                className={
                    row.isWarning
                        ? 'mt-1 text-xs text-[var(--crowdin-warning)]'
                        : 'mt-1 text-xs text-[var(--crowdin-text-muted)]'
                }
            >
                {row.description}
            </div>
        );
    }

    function openModal(modal: any) {
        if (!modal) {
            return;
        }

        modal.style.display = 'block';
        modal.open();
    }

    function closeModal(modal: any) {
        if (!modal) {
            return;
        }

        modal.style.display = 'none';
        modal.close();
    }

    function settingsFieldChange(eventOrElement: any) {
        const el = eventOrElement?.currentTarget || eventOrElement?.target || eventOrElement;

        el?.removeAttribute?.('error');
        el?.removeAttribute?.('error-text');
    }

    function isFolder(e: any) {
        return !e.type && (e.nodeType === undefined || e.nodeType === folderType || e.nodeType === branchType);
    }

    return (
        <div>
            <div className="mx-auto max-w-[1420px] px-6 py-4">
                <div className="mb-2.5 text-right">
                    {notice && (
                        <crowdin-alert
                            id="notice"
                            title={notice.title}
                            {...(!notice.icon ? { 'no-icon': 'true' } : {})}
                            {...(notice.type ? { type: notice.type } : {})}
                            style={{ display: isNoticeVisible ? 'block' : 'none', marginBottom: '12px' }}
                        >
                            <div className="flex items-center justify-center">
                                <p className="m-0" dangerouslySetInnerHTML={{ __html: notice.content }} />
                            </div>
                            {notice.close && (
                                <crowdin-button
                                    onClick={() => closeAlert('notice')}
                                    class="absolute top-0 right-0"
                                    icon
                                >
                                    close
                                </crowdin-button>
                            )}
                        </crowdin-alert>
                    )}

                    {checkSubscription && (
                        <crowdin-alert
                            id="subscription-info"
                            no-icon="true"
                            type="warning"
                            style={{ display: subscriptionInfo ? 'block' : 'none', marginBottom: '12px' }}
                        >
                            <div className="flex items-center justify-center">
                                <p className="m-0">
                                    {subscriptionInfo ? `Your trial expires in ${subscriptionInfo.daysLeft} days.` : ''}
                                </p>
                                <crowdin-button
                                    class="ml-2"
                                    primary
                                    onClick={() => {
                                        if (subscriptionInfo?.subscribeLink) {
                                            window.open(
                                                subscriptionInfo.subscribeLink,
                                                '_blank',
                                                'noopener,noreferrer',
                                            );
                                        }
                                    }}
                                >
                                    Subscribe
                                </crowdin-button>
                            </div>
                        </crowdin-alert>
                    )}

                    <div id="buttons" className="flex w-full items-center justify-between">
                        <div className="mr-auto">
                            <span style={{ display: isErrorLogsVisible ? undefined : 'none' }}>
                                <crowdin-button
                                    id="show-integration-btn"
                                    icon-before="arrow_back"
                                    onClick={() => showIntegration()}
                                >
                                    Integration
                                </crowdin-button>
                            </span>
                            <span style={{ display: isErrorLogsVisible ? 'none' : undefined }}>
                                <crowdin-button
                                    id="show-error-logs-btn"
                                    icon-before="list"
                                    onClick={() => showErrorLogs()}
                                >
                                    Error logs
                                </crowdin-button>
                            </span>
                        </div>
                        {isManager && (
                            <crowdin-button icon-before="link" onClick={() => showPermissionsDialog()}>
                                Share
                            </crowdin-button>
                        )}
                        {infoModal && (
                            <crowdin-button icon-before="info" onClick={() => openModal(infoModalRef.current)}>
                                {infoModal.title}
                            </crowdin-button>
                        )}
                        {configurationFields && (
                            <crowdin-button
                                icon-before="settings"
                                onClick={() => {
                                    openModal(settingsModalRef.current);
                                    fillSettingsForm();
                                }}
                            >
                                Settings
                            </crowdin-button>
                        )}
                        <crowdin-button
                            {...(!isOwner ? { disabled: true, title: 'Only the owner can log out' } : {})}
                            icon-before="account_circle"
                            onClick={() => integrationLogout()}
                        >
                            Log out
                        </crowdin-button>
                    </div>
                </div>

                <div style={{ display: isErrorLogsVisible ? 'none' : undefined }}>
                    <crowdin-simple-integration
                        ref={appComponent}
                        async-progress
                        {...(syncNewElements?.crowdin ? { 'skip-crowdin-auto-schedule': true } : {})}
                        {...(syncNewElements?.integration ? { 'skip-integration-auto-schedule': true } : {})}
                        {...(withCronSync?.crowdin || webhooks?.crowdin ? { 'crowdin-schedule': 'true' } : {})}
                        {...(withCronSync?.integration || webhooks?.integration
                            ? { 'integration-schedule': 'true' }
                            : {})}
                        {...(integrationOneLevelFetching ? { 'integration-one-level-fetching': 'true' } : {})}
                        {...(integrationSearchListener ? { 'allow-integration-filter-change-listener': 'true' } : {})}
                        {...(integrationPagination ? { 'integration-load-more-files': 'true' } : {})}
                        integration-name={name}
                        integration-logo="logo.png"
                        {...((integrationButtonMenuItems?.length ?? 0) > 0
                            ? { 'integration-button-menu-items': integrationButtonMenuItems }
                            : {})}
                        {...((crowdinButtonMenuItems?.length ?? 0) > 0
                            ? { 'crowdin-button-menu-items': crowdinButtonMenuItems }
                            : {})}
                        {...(filtering?.crowdinLanguages ? { 'crowdin-filter': true } : {})}
                        {...(filtering?.integrationFilterConfig
                            ? {
                                  'integration-filter': true,
                                  'integration-filter-config': filtering.integrationFilterConfig,
                              }
                            : {})}
                    ></crowdin-simple-integration>
                </div>

                <div id="user-errors" style={{ display: isErrorLogsVisible ? undefined : 'none' }}>
                    <crowdin-show-as-table
                        ref={userErrorsTableRef}
                        is-loading
                        id="user-errors-table"
                        is-searchable
                        total-records="25"
                        search-placeholder="Search something"
                    ></crowdin-show-as-table>
                    <crowdin-alert>
                        This table displays the most recent error logs from the past month. Logs older than one month
                        will be automatically deleted.
                    </crowdin-alert>
                </div>
            </div>

            <crowdin-toasts></crowdin-toasts>
            <crowdin-async-progress ref={asyncProgressRef} cancelAsyncAction=""></crowdin-async-progress>

            {/* Subscription Modal */}
            <crowdin-modal
                ref={subscriptionModal}
                style={{ display: 'none' }}
                id="subscription-modal"
                modal-width="50"
                close-button="false"
            >
                <div>
                    <crowdin-alert type="warning">Subscribe to continue using the {name} app.</crowdin-alert>
                </div>
                <div slot="footer">
                    <crowdin-button
                        primary
                        onClick={() => window.open(subscriptionLink.current, '_blank', 'noopener,noreferrer')}
                    >
                        Subscribe
                    </crowdin-button>
                    <crowdin-button
                        outlined
                        onClick={() => window.open('https://crowdin.com/contacts', '_blank', 'noopener,noreferrer')}
                    >
                        Contact us
                    </crowdin-button>
                    <crowdin-button class="ml-2.5" secondary onClick={() => integrationLogout()}>
                        Log out
                    </crowdin-button>
                </div>
            </crowdin-modal>

            {/* Permissions Modal */}
            <crowdin-modal
                ref={permissionsModalRef}
                style={{ display: 'none' }}
                id="permissions-modal"
                modal-title="Share"
                close-button-title="Close"
                close-button
                body-overflow-unset
            >
                <div
                    className={
                        modalLoaders['permissions-modal']
                            ? 'absolute inset-0 z-[4] flex items-center justify-center bg-white/30'
                            : 'hidden'
                    }
                >
                    <crowdin-progress-indicator></crowdin-progress-indicator>
                </div>
                <div>
                    <div>
                        <crowdin-users-select
                            ref={usersSelectRef}
                            allow-new-options
                            is-multi
                            is-searchable
                            id="users"
                            key="users"
                            label="Users"
                            help-text="Search for members by name, username, email, or invite new ones using their email."
                            is-position-fixed
                        >
                            {permissionUserOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            ))}
                        </crowdin-users-select>
                    </div>
                    <div>
                        <div className={isPermissionsConfirmVisible ? 'mt-4' : 'hidden'}>
                            <table className="w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th
                                            className="border-b border-[var(--crowdin-border-color)] p-4 text-left text-sm"
                                            style={{ width: '40%' }}
                                        >
                                            Access
                                        </th>
                                        <th
                                            className="border-b border-[var(--crowdin-border-color)] p-4 text-left text-sm"
                                            style={{ width: '35%' }}
                                        >
                                            Users
                                        </th>
                                        <th
                                            className="border-b border-[var(--crowdin-border-color)] p-4 text-right text-sm"
                                            style={{ width: '20%' }}
                                        >
                                            Upcoming changes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hasOrganization && (
                                        <tr>
                                            <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                                <crowdin-p>Registration in Organization</crowdin-p>
                                                {renderPermissionRowDescription(permissionsSummary?.organization)}
                                            </td>
                                            <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                                {renderPermissionUsers(permissionsSummary?.organization)}
                                            </td>
                                            <td className="border-b border-[var(--crowdin-border-color)] p-4 text-right align-top">
                                                {permissionsSummary?.organization &&
                                                    renderPermissionStatus(permissionsSummary.organization.status)}
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                            <crowdin-p>Project Manager Access</crowdin-p>
                                        </td>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                            {renderPermissionUsers(permissionsSummary?.project)}
                                        </td>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-right align-top">
                                            {permissionsSummary?.project &&
                                                renderPermissionStatus(permissionsSummary.project.status)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                            <crowdin-p>Application Visibility Access</crowdin-p>
                                            {renderPermissionRowDescription(permissionsSummary?.applicationSettings)}
                                        </td>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                            {renderPermissionUsers(permissionsSummary?.applicationSettings)}
                                        </td>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-right align-top">
                                            {permissionsSummary?.applicationSettings &&
                                                renderPermissionStatus(permissionsSummary.applicationSettings.status)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                            <crowdin-p>Access to the Integration</crowdin-p>
                                            <div className="mt-1 text-xs text-[var(--crowdin-text-muted)]">
                                                This provides access to sync files and modify the current integration
                                                settings.
                                            </div>
                                        </td>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-left align-top">
                                            {renderPermissionUsers(permissionsSummary?.applicationCredentials)}
                                        </td>
                                        <td className="border-b border-[var(--crowdin-border-color)] p-4 text-right align-top">
                                            {permissionsSummary?.applicationCredentials &&
                                                renderPermissionStatus(
                                                    permissionsSummary.applicationCredentials.status,
                                                )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {zenModeUrl && (
                            <div className="mt-4">
                                <crowdin-input
                                    with-fixed-height
                                    label="Zen Mode link"
                                    help-text="This focused view allows you to concentrate solely on the Integrations section, eliminating other distractions."
                                    value={zenModeUrl}
                                    name="zenModeLink"
                                    with-copy-button
                                ></crowdin-input>
                            </div>
                        )}
                    </div>
                </div>
                <div slot="footer">
                    <crowdin-button id="confirm-users-btn" outlined onClick={() => inviteUsers()}>
                        Save
                    </crowdin-button>
                </div>
            </crowdin-modal>

            {/* Excluded Languages Modal */}
            {excludedTargetLanguages && (
                <crowdin-modal
                    ref={languageModal}
                    style={{ display: 'none' }}
                    modal-width="50"
                    modal-title="Select Target Languages for Translation"
                    id="excluded-languages"
                >
                    {languageModalState && (
                        <>
                            <div>
                                <crowdin-select
                                    ref={languageSelectRef}
                                    is-multi
                                    is-searchable
                                    is-position-fixed
                                    close-on-select="false"
                                    name="languages"
                                    id="language-select"
                                    label="Languages"
                                >
                                    {languageModalState.languages.map((language) => (
                                        <option key={language.id} value={language.id}>
                                            {language.name}
                                        </option>
                                    ))}
                                </crowdin-select>
                            </div>
                            <div slot="footer">
                                <span hidden={languageModalState.action !== 'updateCrowdinTargetLanguages'}>
                                    <crowdin-button
                                        id="update-crowdin-target-languages"
                                        outlined
                                        onClick={() => updateCrowdinFilesTargetLanguages()}
                                    >
                                        Update Target Languages
                                    </crowdin-button>
                                </span>
                                <span hidden={languageModalState.action !== 'syncToCrowdin'}>
                                    <crowdin-button
                                        id="upload-exclude-languages"
                                        outlined
                                        onClick={() => uploadFilesToCrowdinWithExcludedLanguages()}
                                    >
                                        <div className="inline-flex items-center gap-1">
                                            <span>Sync to</span>
                                            <crowdin-logo is-icon />
                                        </div>
                                    </crowdin-button>
                                </span>
                            </div>
                        </>
                    )}
                </crowdin-modal>
            )}

            {/* Info Modal */}
            {infoModal && (
                <crowdin-modal
                    ref={infoModalRef}
                    style={{ display: 'none' }}
                    id="info-modal"
                    modal-width="50"
                    modal-title={infoModal.title}
                    close-button-title="Close"
                >
                    <div dangerouslySetInnerHTML={{ __html: infoModal.content }} />
                </crowdin-modal>
            )}

            {/* Settings Modal */}
            {configurationFields && (
                <crowdin-modal
                    ref={settingsModalRef}
                    style={{ display: 'none' }}
                    id="settings-modal"
                    modal-width="65"
                    modal-title="Settings"
                    close-button-title="Close"
                >
                    <div
                        className={
                            modalLoaders['settings-modal']
                                ? 'absolute inset-0 z-[4] flex items-center justify-center bg-white/30'
                                : 'hidden'
                        }
                    >
                        <crowdin-progress-indicator></crowdin-progress-indicator>
                    </div>
                    <div id="modal-content">
                        <crowdin-tabs
                            tabs-config={JSON.stringify(configurationFields.map((f) => f.name))}
                            active-tab="1"
                        >
                            {configurationFields.map((tab, tabIndex) => (
                                <div key={tabIndex} slot={`tab-${tabIndex + 1}`}>
                                    <div>
                                        {tab.fields.map((field, fieldIndex) => (
                                            <Fragment key={field.key || `${tabIndex}-${fieldIndex}`}>
                                                {renderConfigField(field, settingsFieldChange, registerSettingsField)}
                                                <div
                                                    style={{ padding: '8px' }}
                                                    {...(field.dependencySettings
                                                        ? { 'data-dependency': field.dependencySettings }
                                                        : {})}
                                                ></div>
                                            </Fragment>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </crowdin-tabs>
                    </div>
                    <div slot="footer">
                        <crowdin-button
                            id="settings-save-btn"
                            outlined
                            disabled={isSettingsSaveDisabled}
                            onClick={() => saveSettings()}
                        >
                            Save
                        </crowdin-button>
                    </div>
                </crowdin-modal>
            )}

            {/* Confirm Schedule Modal */}
            {(syncNewElements?.crowdin || syncNewElements?.integration) && (
                <crowdin-modal
                    ref={scheduleModalRef}
                    style={{ display: 'none' }}
                    id="confirm-schedule-modal"
                    modal-width="50"
                    modal-title="Synchronization options"
                    close-button-title="Close"
                    close-button="true"
                    body-overflow-unset
                >
                    <crowdin-checkbox
                        ref={selectedFilesCheckboxRef}
                        id="selected-files"
                        name="selected-files"
                        label="Selected files"
                        class="hydrated"
                        checked={scheduleModalState.selectedFiles}
                        value={String(scheduleModalState.selectedFiles)}
                        onChange={() => onChangeAutoSynchronizationOptions()}
                    ></crowdin-checkbox>
                    <crowdin-checkbox
                        ref={allFolderFilesCheckboxRef}
                        id="all-folder-files"
                        name="all-folder-files"
                        label="All files in the folder"
                        class="hydrated"
                        hidden={!scheduleModalState.showAllFolderFiles}
                        checked={scheduleModalState.allFolderFiles}
                        value={String(scheduleModalState.allFolderFiles)}
                        onChange={() => onChangeAutoSynchronizationOptions()}
                    ></crowdin-checkbox>
                    <crowdin-checkbox
                        ref={newFilesCheckboxRef}
                        id="new-files"
                        name="new-files"
                        label="New files"
                        class="hydrated"
                        hidden={!scheduleModalState.showNewFiles}
                        checked={scheduleModalState.newFiles}
                        value={String(scheduleModalState.newFiles)}
                        {...(scheduleModalState.newFilesClearSelection ? { 'clear-selection': '' } : {})}
                        onChange={() => onChangeAutoSynchronizationOptions()}
                    ></crowdin-checkbox>
                    <div slot="footer">
                        <crowdin-button
                            outlined
                            id="save-schedule-sync"
                            disabled={isScheduleSaveDisabled}
                            onClick={() => saveScheduleSync()}
                        >
                            Save
                        </crowdin-button>
                    </div>
                </crowdin-modal>
            )}

            {/* User Error Detail Modal */}
            <crowdin-modal
                ref={userErrorDetailModalRef}
                style={{ display: 'none' }}
                id="user-error-detail"
                close-button-title="Close"
                close-button="true"
            >
                {selectedUserError && getUserErrorDetail(selectedUserError)}
            </crowdin-modal>
        </div>
    );
};
